import { useState } from 'react'
import { t } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import { Grid, Typography, Stack } from '@mui/material'

import { useGetNetworkQuery } from '@/core/services/network'
import { useGetPlanningOverviewQuery, useGetPlanningStatsQuery } from '@/core/services/planning'
import { CreatePlanningDialog } from '@/shared/plannings'

import { PlanningStatistics } from './PlanningStatistics'
import { CriticalAttentionSection } from './CriticalAttentionSection'
import { PlanningsOverviewTable } from './PlanningsOverviewTable'
import { PlanningDashboardActions } from './PlanningDashboardActions'

type NetworkPlanningsDashboardProps = {
  networkId: string
}

export function NetworkPlanningsDashboard({ networkId }: NetworkPlanningsDashboardProps) {
  useLingui()

  const [showArchived, setShowArchived] = useState(false)

  const { data: network } = useGetNetworkQuery(networkId)

  const {
    data: planningOverview = [],
    isLoading: isLoadingOverview,
    isFetching: isFetchingOverview,
    refetch: refetchOverview,
  } = useGetPlanningOverviewQuery(
    {
      includeArchived: showArchived,
      networkId,
    },
    {
      refetchOnMountOrArgChange: true,
    }
  )

  const {
    data: planningStats,
    isLoading: isLoadingStats,
    isFetching: isFetchingStats,
    refetch: refetchStats,
  } = useGetPlanningStatsQuery(
    { networkId },
    {
      refetchOnMountOrArgChange: true,
    }
  )

  const isRefreshing = isFetchingOverview || isFetchingStats
  const hasAttention = planningOverview.some((planning) => planning.attentionLevel === 'high' || planning.attentionLevel === 'medium')

  // Function to refresh all data
  const handleRefresh = async () => {
    await Promise.all([refetchOverview(), refetchStats()])
  }

  if (!network) return null

  return (
    <Stack data-testid="planning-dashboard" height="100%" spacing={{ xs: 1.5, md: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="h5" component="h1" sx={{ display: { xs: 'none', md: 'block' } }}>
          {t`Planning Dashboard - ${network.name}`}
        </Typography>
        <Stack direction="row" flex={1} alignItems="center" justifyContent="end" spacing={1}>
          <CreatePlanningDialog visibility="network" visibilityId={networkId} />
          <PlanningDashboardActions showArchived={showArchived} setShowArchived={setShowArchived} isRefreshing={isRefreshing} onRefresh={handleRefresh} />
        </Stack>
      </Stack>
      <div>
        <Grid container spacing={{ xs: 1.5, md: 3 }}>
          <Grid item xs={12}>
            <PlanningStatistics stats={planningStats} isLoading={isLoadingStats} />
          </Grid>
          <Grid item xs={12} md={4} sx={{ display: { xs: isLoadingOverview || hasAttention ? 'block' : 'none', md: 'block' } }}>
            <CriticalAttentionSection plannings={planningOverview} isLoading={isLoadingOverview} />
          </Grid>
          <Grid item xs={12} md={8}>
            <PlanningsOverviewTable
              isLoading={isLoadingOverview}
              onArchiveCompleted={handleRefresh}
              onUnarchiveCompleted={handleRefresh}
              onDeleteCompleted={handleRefresh}
              plannings={planningOverview}
            />
          </Grid>
        </Grid>
      </div>
    </Stack>
  )
}
